import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import {
  ModalButton,
  MoreOptions,
  TextWithLineBreaks,
  User
} from 'components/common';
import {
  ProfileCompetenciesEndorsements,
  ProfileCompetenciesEndorsementsDelete,
  ProfileCompetenciesEndorsementsEdit,
  ProfileCompetenciesEndorsementsEndorse
} from '.';

import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';
import { Box } from 'components/common/box';
import { EditedContent } from 'components/common/notifications';

interface Props {
  competency: MergedCompetency;
  renderAsFixed?: boolean;
  targetUserId: number;
  updateCompetencies: () => Promise<void>;
}

const ProfileCompetenciesEndorsementsActions = ({
  competency,
  renderAsFixed,
  targetUserId,
  updateCompetencies
}: Props) => {
  const { messages, theme, user } = useReduxState();

  const endorsements = competency.endorsements?.length
    ? competency.endorsements
    : competency.levels
    ? competency.levels.flatMap((level) => level.endorsements)
    : null;

  const usersOwnEndorsement = endorsements?.find(
    (endorsement) => endorsement.EndorserUserId === user?.id
  );

  if (
    !endorsements ||
    (Array.isArray(endorsements) && endorsements.length === 0)
  ) {
    return (
      <div>
        {targetUserId !== user?.id && (
          <MoreOptions renderAsFixed={renderAsFixed} style={{ marginLeft: 10 }}>
            <ProfileCompetenciesEndorsementsEndorse
              competency={competency}
              targetUserId={targetUserId}
              updateCompetencies={updateCompetencies}
            />
          </MoreOptions>
        )}
      </div>
    );
  }

  return (
    <div>
      <ModalButton
        category="lighter"
        icon={['far', 'thumbs-up']}
        size="small"
        isRounded={true}
        text={getMessageWithPlaceholders({
          message: usersOwnEndorsement
            ? endorsements.length > 1
              ? messages.competenciesEndorsementsYouAndUsers
              : messages.competenciesEndorsementsYouEndorsed
            : messages.competenciesEndorsementsNumberOf,
          placeholders: {
            numberOfEndorsements:
              usersOwnEndorsement && endorsements.length > 1
                ? endorsements.length - 1
                : endorsements.length
          }
        })}
        modalContentStyle={{ backgroundColor: theme.lightGreyLighter }}
        modalProps={{
          closeButton: true,
          title: messages.competenciesEndorsements,
          width: 'medium'
        }}
        {...{ isRounded: true }}
      >
        <ProfileCompetenciesEndorsements
          competency={competency}
          updateCompetencies={updateCompetencies}
        />
      </ModalButton>

      {targetUserId !== user?.id && (
        <MoreOptions renderAsFixed={renderAsFixed} style={{ marginLeft: 15 }}>
          {usersOwnEndorsement ? (
            <>
              <ProfileCompetenciesEndorsementsEdit
                competency={competency}
                endorsement={usersOwnEndorsement}
                updateCompetencies={updateCompetencies}
              />
              <ProfileCompetenciesEndorsementsDelete
                endorsement={usersOwnEndorsement}
                updateCompetencies={updateCompetencies}
              />
            </>
          ) : (
            <>
              <ProfileCompetenciesEndorsementsEndorse
                competency={competency}
                targetUserId={targetUserId}
                updateCompetencies={updateCompetencies}
              />
            </>
          )}
        </MoreOptions>
      )}
    </div>
  );
};

export default ProfileCompetenciesEndorsementsActions;
